// src/components/OTPInput.jsx
// Centered OTP UI with:
// - helper text ABOVE inputs
// - dismissible error chip (bigger padding, safe close button)
// - "Send new code" link always visible (disabled state if cooling down or rate-limited)
// - countdown under link: cooldown seconds or long window if provided
// - ENTER triggers onEnter

import React, { useEffect, useRef, useState } from 'react';
import '../styles/theme.css';

export default function OTPInput({
	value,
	onChange,
	length = 6,
	disabled = false,
	onEnter,
	onResend,
	cooldownSeconds = 0,
	rateLimited = false,
	retryAfterSeconds = 0, // OPTIONAL: pass from backend to show long window countdown
	helpText,
	errorText, // e.g. "Code Invalid" or "Code Expired"
}) {
	const refs = useRef(Array.from({ length }, () => React.createRef()));
	const [secondsLeft, setSecondsLeft] = useState(Math.max(0, Math.floor(cooldownSeconds)));
	const [errorDismissed, setErrorDismissed] = useState(false);
	const lastAutoSubmittedValue = useRef('');

	// reset dismissal when a fresh error arrives
	useEffect(() => setErrorDismissed(false), [errorText]);

	// CRITICAL FIX: Auto-submit when OTP is complete
	// Track the last auto-submitted value to prevent re-submitting the same code
	// but allow auto-submit when user corrects the code
	useEffect(() => {
		const currentValue = value || '';
		// Only auto-submit if:
		// 1. Code is complete (length === 6)
		// 2. Not disabled
		// 3. This is a different value than what we last auto-submitted
		if (currentValue.length === length && !disabled && currentValue !== lastAutoSubmittedValue.current) {
			// console.log('[OTPInput] Auto-submit: Code complete, triggering verification');
			lastAutoSubmittedValue.current = currentValue;
			// Small delay to ensure UI updates before submission
			setTimeout(() => {
				if (typeof onEnter === 'function') {
					// console.log('[OTPInput] Auto-submit: Calling onEnter()');
					onEnter();
				}
			}, 150);
		}
		// Reset the tracked value when user clears/shortens the code
		if (currentValue.length < length) {
			lastAutoSubmittedValue.current = '';
		}
	}, [value, length, disabled, onEnter]);

	// reset cooldown when backend updates it
	useEffect(() => {
		setSecondsLeft(Math.max(0, Math.floor(cooldownSeconds)));
	}, [cooldownSeconds]);

	// tick cooldown seconds
	useEffect(() => {
		if (secondsLeft <= 0) return;
		const id = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
		return () => clearInterval(id);
	}, [secondsLeft]);

	// paste support (allow pasting the code)
	useEffect(() => {
		function onPaste(e) {
			const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, length);
			if (!text) return;
			const merged = (value + text).slice(0, length);
			onChange(merged);
			e.preventDefault();
			// Auto-submit is now handled by useEffect watching the value
		}
		const node = refs.current[0].current?.parentElement;
		node?.addEventListener('paste', onPaste);
		return () => node?.removeEventListener('paste', onPaste);
	}, [length, onChange, value, onEnter]);

	function setAt(i, v) {
		const d = value || '';
		const next = (d.slice(0, i) + v + d.slice(i + 1)).slice(0, length);
		onChange(next);
		// Auto-submit is now handled by useEffect watching the value
	}

	function keyHandler(e, i) {
		if (e.key === 'Enter' && typeof onEnter === 'function') onEnter();
		if (e.key === 'Backspace' && !(value || '')[i] && refs.current[i - 1]) refs.current[i - 1].current.focus();
		if (e.key === 'ArrowLeft' && refs.current[i - 1]) refs.current[i - 1].current.focus();
		if (e.key === 'ArrowRight' && refs.current[i + 1]) refs.current[i + 1].current.focus();
	}

	// link is always shown; decide if it's enabled
	const isCoolingDown = secondsLeft > 0;
	const isRateLimited = !!rateLimited;
	const resendEnabled = !!onResend && !disabled && !isCoolingDown && !isRateLimited;

	// format long retry window if provided (e.g., 1h 12m)
	function formatLongWindow(total) {
		const t = Math.max(0, Math.floor(total || 0));
		const h = Math.floor(t / 3600);
		const m = Math.floor((t % 3600) / 60);
		const s = t % 60;
		if (h) return `${h}h ${m}m`;
		if (m) return `${m}m ${s ? `${s}s` : ''}`.trim();
		return `${s}s`;
	}

	return (
		<div style={{ textAlign: 'center' }}>
			{/* Helper ABOVE inputs */}
			{helpText && (
				<div
					style={{
						fontSize: 14,
						color: '#ffffff',
						opacity: 0.9,
						lineHeight: 1.4,
						maxWidth: 360,
						margin: '0 auto 10px',
					}}
				>
					{helpText}
				</div>
			)}

			{/* OTP inputs */}
			<div style={{ display: 'flex', gap: 'clamp(6px, 2vw, 10px)', justifyContent: 'center', flexWrap: 'wrap' }}>
				{Array.from({ length }).map((_, i) => (
					<input
						key={i}
						ref={refs.current[i]}
						className="input otp-input"
						style={{
							width: 'clamp(40px, 10vw, 48px)',
							minHeight: '48px',
							textAlign: 'center',
							letterSpacing: 2,
							padding: '0.7rem 0',
							// CRITICAL: Minimum 20px to prevent iOS zoom (16px threshold)
							// On iPhone (375px), 4vw = 15px which would cause zoom
							fontSize: 'clamp(20px, 4vw, 22px)',
							fontWeight: 600
						}}
						type="text"
						inputMode="numeric"
						maxLength={length}
						autoComplete={i === 0 ? 'one-time-code' : 'off'}
						value={(value || '')[i] || ''}
						onChange={e => {
							const inputValue = e.target.value.replace(/\D/g, '');

							// Handle SMS autofill that might paste all 6 digits into first input
							if (i === 0 && inputValue.length > 1) {
								const fullCode = inputValue.slice(0, length);
								onChange(fullCode);
								// Auto-submit is now handled by useEffect watching the value

								// Focus the last input to show completion
								if (fullCode.length === length && refs.current[length - 1]) {
									setTimeout(() => refs.current[length - 1].current?.focus(), 0);
								}
								return;
							}

							// Normal single-digit input
							const c = inputValue.slice(-1);
							setAt(i, c);
							if (c && refs.current[i + 1]) refs.current[i + 1].current.focus();
						}}
						onInput={e => {
							// Additional handler for iOS autofill (backup for onChange)
							const inputValue = e.target.value.replace(/\D/g, '');
							if (i === 0 && inputValue.length > 1 && inputValue.length <= length) {
								const fullCode = inputValue.slice(0, length);
								onChange(fullCode);
								// Auto-submit is now handled by useEffect watching the value

								// Focus the last input
								if (fullCode.length === length && refs.current[length - 1]) {
									setTimeout(() => refs.current[length - 1].current?.focus(), 0);
								}
							}
						}}
						onKeyDown={e => keyHandler(e, i)}
						disabled={disabled}
						aria-label={`Digit ${i + 1}`}
					/>
				))}
			</div>

			{/* breathing room under inputs */}
			<div style={{ height: 18 }} />

			{/* error chip (dismissible) — between inputs and Verify */}
			{errorText && !errorDismissed && (
				<div
					role="alert"
					style={{
						margin: '0 auto 10px',
						maxWidth: 420,
						background: 'rgba(239,68,68,0.12)', // soft red
						border: '1px solid rgba(239,68,68,0.55)',
						color: '#fecaca',
						borderRadius: 12,
						padding: '18px 52px 18px 18px', // ample padding so ✕ never overlaps
						position: 'relative',
						textAlign: 'left',
						backdropFilter: 'blur(2px)',
					}}
				>
					<button
						aria-label="Dismiss"
						onClick={() => setErrorDismissed(true)}
						style={{
							position: 'absolute',
							top: 10,
							right: 12,
							width: 28,
							height: 28,
							display: 'grid',
							placeItems: 'center',
							background: 'transparent',
							border: 'none',
							color: '#fecaca',
							fontSize: 18,
							lineHeight: 1,
							cursor: 'pointer',
							borderRadius: 6,
						}}
					>
						×
					</button>
					<div style={{ fontSize: 14, fontWeight: 600 }}>{errorText}</div>
				</div>
			)}

			{/* resend zone – link always shown; disabled when cooling down or rate-limited */}
			<div style={{ marginTop: 6 }}>
				<button
					type="button"
					onClick={resendEnabled ? onResend : undefined}
					aria-disabled={!resendEnabled}
					style={{
						background: 'none',
						border: 'none',
						color: '#ffffff',
						textDecoration: 'underline',
						fontSize: 14,
						padding: 0,
						cursor: resendEnabled ? 'pointer' : 'not-allowed',
						opacity: resendEnabled ? 1 : 0.6,
					}}
				>
					Send new code
				</button>

				{/* countdown / window hint */}
				{(isCoolingDown || isRateLimited) && (
					<div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
						{isRateLimited && retryAfterSeconds > 0 ? `Available in ${formatLongWindow(retryAfterSeconds)}` : isCoolingDown ? `Available in ${secondsLeft}s` : ''}
					</div>
				)}
			</div>
		</div>
	);
}
